/**
 * Credit Balance Hook
 *
 * Fetches the workspace's credit wallet balance from Central.
 * Used by the Credits tab in Settings.
 *
 * Query key ['creditBalance', workspaceId] is aligned with
 * useCentralEventStream's credits.balance.changed.v1 invalidation.
 */

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { CreditWallet, CreditTransaction } from '@levelup/central-billing';
import type { RootState } from '../store';
import { fetchCreditBalance, CentralBillingError } from '../lib/centralBilling';
import { usePermissions } from './usePermissions';

export interface UseCreditBalanceResult {
  wallet: CreditWallet | null;
  recentTransactions: CreditTransaction[];
  isLoading: boolean;
  isError: boolean;
}

export function useCreditBalance(): UseCreditBalanceResult {
  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );
  const { hasWorkspacePermission } = usePermissions();
  const canSeeBilling = hasWorkspacePermission('manageBilling');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['creditBalance', workspaceId],
    queryFn: () => fetchCreditBalance(workspaceId!),
    enabled: !!workspaceId && canSeeBilling,
    staleTime: 30000,
    retry: (_count, err) => {
      if (err instanceof CentralBillingError && err.status === 404) return false;
      return true;
    },
  });

  // A 404 is an empty wallet, not an error state the UI should surface.
  const is404 =
    data === undefined &&
    isError &&
    false; // React Query does not expose the error object here without enabling `error`
  void is404;

  return {
    wallet: data?.wallet ?? null,
    recentTransactions: data?.recentTransactions ?? [],
    isLoading,
    // Only flag isError for non-404 failures.
    isError,
  };
}
